import { useBackend } from '@/hooks/useBackend';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useDialogStore } from '@/stores/dialogStore';
import { useTree } from '@/hooks/useTree';
import { useTreeStore } from '@/stores/treeStore';
import { log } from '@/lib/utils/logger';
import { TreeNode } from '@/types/tree';
import { TreeNodeFactory } from '@/lib/tree/treeNodeFactory';
import { refreshAfterAction } from '@/lib/tree/submitEntityUtils';
import { performResetOperation, handleResetError } from '@/lib/tree/resetEntityUtils';

/**
 * Custom hook for resetting entities with consistent logic across the app
 */
export const useResetEntity = () => {
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();
  const { showResetDialog, showConfirmDialog } = useDialogStore();
  const { getData, reloadNode, clearChildrenLoaded, invalidateChildrenCache, loadChildren } = useTree();
  const expandNode = useTreeStore((state) => state.expandNode);
  const setLoading = useTreeStore((state) => state.setLoading);
  const markResetting = (dataId: string, flag: boolean) => setLoading(`reset-${dataId}`, flag);

  /**
   * Resets an entity with the same behavior as tree context-menu reset:
   * reset node, reload node, and refresh parent subtree when applicable.
   */
  const resetEntity = async (node: TreeNode): Promise<boolean> => {
    try {
      markResetting(node.nodeId, true);

      const confirmed = await showResetDialog(TreeNodeFactory.getNodeTypeLabel(node), node.label);
      if (!confirmed) {
        return false;
      }

      try {
        await performResetOperation(node, backend);
        await refreshAfterAction(
          node,
          backend,
          {
            reloadNode,
            clearChildrenLoaded,
            invalidateChildrenCache,
            loadChildren,
            expandNode,
          },
          { reloadParentSubtree: true }
        );
        const entityTypeLabel = TreeNodeFactory.getNodeTypeLabel(node, getData);
        showSnackbar(`${entityTypeLabel} ${node.label} reset successfully`, 'success');
        return true;
      } catch (err) {
        const errorResult = await handleResetError(err, node, {
          showSnackbar,
          showConfirmDialog,
          getData,
        });

        if (errorResult.shouldRetry) {
          await performResetOperation(node, backend);
          await refreshAfterAction(
            node,
            backend,
            {
              reloadNode,
              clearChildrenLoaded,
              invalidateChildrenCache,
              loadChildren,
              expandNode,
            },
            { reloadParentSubtree: true }
          );
          const entityTypeLabel = TreeNodeFactory.getNodeTypeLabel(node, getData);
          showSnackbar(`${entityTypeLabel} ${node.label} reset successfully`, 'success');
          return true;
        }
        return false;
      }
    } catch (error) {
      log.error('[useResetEntity] Unexpected error in resetEntity:', error);
      showSnackbar('Unexpected error occurred while resetting.', 'error');
      return false;
    } finally {
      markResetting(node.nodeId, false);
    }
  };

  return {
    resetEntity,
  };
};
